import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Slide from "@mui/material/Slide"
import Modal from "@mui/material/Modal"
import { UNAUTHENTICATED_USER } from "constants/errors"
import { NAVIGATE_TO_FEED_PAGE } from "constants/events"
import { PWA_INSTALLATION_GUIDE } from "constants/storages"
import { isRunningStandalone } from "utils/navigatorUtils"
import useEventListener from "hooks/useEventListener"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useAppSelector } from "appRedux/hooks"
import { useAuth } from "context/AuthContext"
import Toast from "components/atoms/Toast"
import PageTitle from "components/atoms/PageTitle"
import ResetPasswordFlow from "components/organisms/ResetPasswordFlow"
import PWAInstallationGuide from "components/templates/PWAInstallationGuide"

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const [showPWAInstallationGuide, setShowPWAInstallationGuide] = useState<boolean>(false)

  const globalToastMessage = useAppSelector((state) => state.global.message)

  const { isAuthenticated, user } = useAuth()

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  useEventListener(document, "showUnauthenticatedUserError", handleShowUnauthenticatedUserError)

  useEventListener(document, "storage", updateShowPWAInstallationGuide)

  useEventListener(document, NAVIGATE_TO_FEED_PAGE, handleNavigateToFeedPage)

  useEffect(() => {
    if (isAuthenticated && user) {
      document.dispatchEvent(new CustomEvent(NAVIGATE_TO_FEED_PAGE))
    }
    updateShowPWAInstallationGuide()
  }, [])

  function handleShowUnauthenticatedUserError(event: CustomEvent) {
    const { status, message } = event.detail
    dispatch(updateGlobalToastMessage({
      severity: "error",
      key: UNAUTHENTICATED_USER,
      show: true,
      status: status,
      message: message
    }))
  }

  function updateShowPWAInstallationGuide() {
    const seenPWAInstallationGuide = localStorage.getItem(PWA_INSTALLATION_GUIDE)
    const shouldShowInstallationGuide = !seenPWAInstallationGuide && !isRunningStandalone() && pathname === "/login"
    setShowPWAInstallationGuide(shouldShowInstallationGuide)
  }

  function handleNavigateToFeedPage() {
    navigate("/feed")
  }

  return (
    <AuthLayoutWrapper>
      <Modal
        open={showPWAInstallationGuide}
        onClose={() => {
        }}
        closeAfterTransition
        keepMounted
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center"
        }}
      >
        <Slide
          direction="up"
          in={showPWAInstallationGuide}
          timeout={400}
          easing={{
            enter: "ease-in-out",
            exit: "linear"
          }}
          style={{ position: "relative" }}
        >
          <InstallationGuideWrapper>
            <PWAInstallationGuide/>
          </InstallationGuideWrapper>
        </Slide>
      </Modal>

      <AuthContainer id="auth-layout">
        <PageTitle/>
        <Stack
          width="100%"
          height="calc(100vh - 48px)"
          alignItems="center"
          justifyContent="space-between"
          flexGrow={1}
          flexShrink={0}
          overflow="scroll"
          marginTop="48px"
        >
          <img
            src="/logo.svg"
            alt="logo"
            style={{
              width: "40%",
              height: "50%",
              maxWidth: "156px",
              margin: "20px 0",
              transform: "translateX(-10px)"
            }}
          />
          {!showPWAInstallationGuide && <AuthFormWrapper>
            {children}
          </AuthFormWrapper>}
        </Stack>

        {pathname.includes("login") && <a
            referrerPolicy="origin"
            target="_blank"
            style={{ maxWidth: "50px", maxHeight: "50px", display: "block", margin: "0 auto" }}
            href="https://trustseal.enamad.ir/?id=3220716&Code=qRLKEkDdSEzbZe2KJbDKbDNBVZBLQX1B">
            <img
                referrerPolicy="origin"
                src="https://trustseal.enamad.ir/logo.aspx?id=3220716&Code=qRLKEkDdSEzbZe2KJbDKbDNBVZBLQX1B"
                alt=""
                style={{ cursor: "pointer" }}
              // @ts-ignore
                code="qRLKEkDdSEzbZe2KJbDKbDNBVZBLQX1B"/>
        </a>}

        {globalToastMessage && (<Toast
          key={globalToastMessage.key}
          open={globalToastMessage.show}
          severity={globalToastMessage.severity}
          message={t(globalToastMessage.message)}
        />)}
      </AuthContainer>

      <ResetPasswordFlow/>
    </AuthLayoutWrapper>
  )
}

const AuthLayoutWrapper = styled("div")(() => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  overflow: "hidden",

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const AuthContainer = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  position: "absolute",
  top: 0,
  backgroundColor: theme.palette.gray["1"],
  justifyContent: "space-between",
  alignItems: "center",

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const AuthFormWrapper = styled("div")(() => ({
  width: "100%",
  padding: "30px 24px",
  marginTop: "auto"
}))

const InstallationGuideWrapper = styled("div")(() => ({
  zIndex: 100,
  borderRadius: 0,
  boxShadow: "none",
  backgroundColor: "transparent"
}))

